import React from 'react';
import ReactECharts from 'echarts-for-react';
import { TrendingUp, Users, CheckCircle2, AlertTriangle, Activity, BarChart2, ShieldCheck, ArrowUpRight } from 'lucide-react';

export const ExecutiveDashboard: React.FC = () => {
  const chartOption = {
    backgroundColor: 'transparent',
    title: { 
      text: 'IEP Goal Completion Trajectory (2026 Academic Year)', 
      left: 'left',
      textStyle: {
        color: '#1c1b1f',
        fontSize: 16,
        fontWeight: 'bold',
        fontFamily: 'Plus Jakarta Sans, sans-serif'
      }
    },
    tooltip: { 
      trigger: 'axis',
      backgroundColor: '#1c1b1f',
      borderColor: '#ffd64e',
      borderWidth: 1,
      textStyle: { color: '#ffffff', fontSize: 12 }
    },
    legend: {
      data: ['Target Progress', 'Actual Clinical Progress'],
      right: 10,
      textStyle: { color: '#676767', fontWeight: 600 }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: { 
      type: 'category', 
      data: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
      axisLine: { lineStyle: { color: '#d4d4d4' } },
      axisLabel: { color: '#676767', fontWeight: 600 }
    },
    yAxis: { 
      type: 'value', 
      name: 'Mastery %',
      max: 100,
      splitLine: { lineStyle: { color: '#f0f0f0' } },
      axisLabel: { color: '#676767', fontWeight: 600 }
    },
    series: [
      {
        name: 'Target Progress',
        type: 'line',
        data: [20, 35, 50, 65, 75, 85, 92, 95, 98],
        smooth: true,
        lineStyle: { width: 3, color: '#1c1b1f' },
        itemStyle: { color: '#1c1b1f' }
      },
      {
        name: 'Actual Clinical Progress',
        type: 'line',
        data: [18, 30, 48, 60, 72, 80, 88, 91, 94],
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(255, 214, 78, 0.45)' },
              { offset: 1, color: 'rgba(255, 214, 78, 0.02)' }
            ]
          }
        },
        lineStyle: { width: 3, color: '#ffd64e' },
        itemStyle: { color: '#ffd64e', borderWidth: 2, borderColor: '#1c1b1f' }
      }
    ]
  };

  const metrics = [
    {
      label: 'Enrolled Students',
      value: '1,420',
      change: '+12.4%',
      desc: 'Active caseload across all 21 RPwD conditions',
      icon: Users,
      badgeColor: 'bg-[#ffd64e] text-black'
    },
    {
      label: 'IEP Goal Success Rate',
      value: '88.4%',
      change: '+4.2%',
      desc: 'Standardized milestone completion metric',
      icon: TrendingUp,
      badgeColor: 'bg-[#bfecc7] text-[#1c1b1f]'
    },
    {
      label: 'Therapy Sessions Logged',
      value: '12,450',
      change: '+28.1%',
      desc: 'Speech, sensory, physical, and ABA logs',
      icon: CheckCircle2,
      badgeColor: 'bg-[#bbcbf4] text-[#1c1b1f]'
    },
    {
      label: 'Clinical Risk Alerts',
      value: '35',
      change: '-18.5%',
      desc: 'Students flagged for IEP regression review',
      icon: AlertTriangle,
      badgeColor: 'bg-[#f9daf3] text-[#1c1b1f]'
    }
  ];

  return (
    <div className="space-y-8 max-w-7xl mx-auto pt-28 pb-16 px-4 sm:px-6">
      
      {/* Clarvos Obsidian Header Bento */}
      <div className="bg-[#1c1b1f] text-white rounded-[32px] p-8 md:p-10 border border-white/10 shadow-2xl flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#ffd64e]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="space-y-2 relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-[#ffd64e] text-black text-[11px] font-black rounded-full uppercase tracking-wider">
            <Activity className="w-3.5 h-3.5 text-black" />
            <span>Executive C-Suite Intelligence</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight">
            Institutional Clinical Command Center
          </h1>
          <p className="text-xs md:text-sm text-slate-300 max-w-2xl font-normal">
            Real-time multi-tenant therapeutic efficacy, IEP milestone progression curves, and longitudinal student risk telemetry.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0 relative z-10">
          <div className="p-4 bg-white/10 border border-white/15 rounded-2xl text-center backdrop-blur-md">
            <div className="text-2xl font-black text-[#ffd64e] font-mono">99.4%</div>
            <div className="text-[10px] text-white/70 uppercase tracking-wider font-bold">Clinical Precision</div>
          </div>
        </div>
      </div>

      {/* Clarvos 4-Bento Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {metrics.map((m, idx) => {
          const Icon = m.icon;
          return (
            <div 
              key={idx}
              className="bg-[#f9f6f0] rounded-[28px] border border-[#1c1b1f]/10 p-6 shadow-sm hover:shadow-xl hover:border-[#ffd64e] transition-all duration-300 space-y-4 flex flex-col justify-between group"
            >
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 rounded-2xl bg-[#1c1b1f] text-[#ffd64e] flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
                  <Icon className="w-6 h-6" />
                </div>
                <span className={`text-[11px] font-black px-2.5 py-1 rounded-full ${m.badgeColor}`}>
                  {m.change}
                </span>
              </div>

              <div>
                <div className="text-3xl font-black text-[#1c1b1f] tracking-tight">{m.value}</div>
                <div className="text-xs font-extrabold text-[#1c1b1f] mt-1">{m.label}</div>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">{m.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Clarvos ECharts Bento Card */}
      <div className="bg-white rounded-[32px] border border-[#1c1b1f]/10 p-6 md:p-8 shadow-sm space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#1c1b1f] text-[#ffd64e]">
              Longitudinal Clinical Modeling
            </span>
            <h2 className="text-xl font-black text-[#1c1b1f] mt-1">Goal Mastery vs. Prescribed Trajectory</h2>
          </div>
          <span className="text-xs font-bold text-slate-500 bg-[#f9f6f0] px-3 py-1.5 rounded-full border border-slate-200 self-start sm:self-auto">
            Updated Today • 32ms Latency
          </span>
        </div>

        <div className="w-full">
          <ReactECharts option={chartOption} style={{ height: '380px' }} />
        </div>
      </div>

    </div>
  );
};
